// Web-grounded deploy guides for embedding the Ultimo Bots chat widget on the
// major website builders, CMS and e-commerce platforms that do not have a
// native Ultimo Bots app (Wix / Webflow / WordPress already have one). Each
// guide drives one Channels card plus its step-by-step modal (ChannelGuide.js).
//
// Researched and adversarially verified per platform against current vendor
// docs (2026). Logos are the real, full-colour official brand marks shipped in
// public/ (sourced from Wikimedia Commons / official brand sites). Fields:
//   key         unique id (also the /public logo filename and the channel key)
//   name        customer-facing display name
//   cardDesc    one-line subtitle on the channel card
//   category    'builder' | 'ecommerce' | 'cms'  (maps to a Channels filter)
//   brand       brand hex (kept for reference / accents)
//   logo        '/x-logo.(svg|png)' real, full-colour, square symbol-only brand
//               mark in public/ (logomark / app icon, no wordmark text) sized to
//               fill the icon tile
//   embed       'widget' (site-wide floating bubble) | 'iframe' (inline embed)
//   intro       1-2 sentence intro shown above the steps
//   planCaveat  optional paid-tier requirement shown above the steps
//   steps       [{ title, body, placeSnippet? }]. body is restricted markdown
//               (**bold**, `code`, [text](url)). placeSnippet marks the single
//               step where the copyable code block is rendered
//   note        optional caveat shown under the steps
//   docUrl      official help-doc URL

export const CHANNEL_GUIDES = [
  {
    "key": "squarespace",
    "name": "Squarespace",
    "cardDesc": "Add the chat widget to your Squarespace site.",
    "category": "builder",
    "brand": "#000000",
    "logo": "/squarespace-logo.svg",
    "embed": "widget",
    "intro": "Squarespace lets you drop a site-wide script into the Footer code injection field, so your Ultimo Bots assistant appears as a floating chat bubble on every page. The whole setup takes just a few minutes.",
    "planCaveat": "Code Injection requires a paid plan. It is available on the current Core, Plus, and Advanced plans, plus older Business and Commerce plans. It is not available on the Personal plan.",
    "steps": [
      {
        "title": "Open your site settings",
        "body": "Log in to Squarespace and open the site you want to add the assistant to. In the left menu, click **Settings**."
      },
      {
        "title": "Go to Code Injection",
        "body": "Click **Advanced**, then click **Code Injection**. On some 7.1 sites this lives under **Website** then **Website Tools** instead. This is where Squarespace lets you add custom HTML and scripts to your whole site."
      },
      {
        "title": "Paste the snippet into the Footer field",
        "body": "Scroll to the **Footer** box and paste the full Ultimo Bots code into it. Squarespace injects Footer code right before the closing `</body>` tag on every page, which is exactly where the chat widget needs to live.",
        "placeSnippet": true
      },
      {
        "title": "Save your changes",
        "body": "Click **Save** at the top of the page to apply the code across your site."
      },
      {
        "title": "Check it on your live site",
        "body": "Open your published site in a new tab or refresh it. The chat bubble should appear in the lower corner. If you do not see it, confirm the snippet is in the **Footer** field and that you saved."
      }
    ],
    "note": "Code Injection is not available on the Personal plan. You need a Core, Plus, or Advanced plan (or a legacy Business or Commerce plan).",
    "docUrl": "https://support.squarespace.com/hc/en-us/articles/205815908-Using-code-injection"
  },
  {
    "key": "godaddy",
    "name": "GoDaddy",
    "cardDesc": "Add the chat widget to your GoDaddy site.",
    "category": "builder",
    "brand": "#1BDBDB",
    "logo": "/godaddy-logo.svg",
    "embed": "iframe",
    "intro": "You can add your Ultimo Bots assistant to a GoDaddy Websites + Marketing (Airo) site using a single HTML section. GoDaddy runs custom code inside a sandboxed, per page block and offers no site-wide code area, so we embed the chat inline on the page where you want it rather than as a floating bubble across the whole site.",
    "planCaveat": "Custom code lives in Websites + Marketing. Some accounts may need a paid plan to publish a custom domain and certain features, so confirm your plan if the HTML section or Publish is unavailable.",
    "steps": [
      {
        "title": "Open your site editor",
        "body": "Sign in to GoDaddy and open your **product page**. Scroll to **Websites + Marketing** and select **Manage** next to your site to open the editor."
      },
      {
        "title": "Go to the page and add a section",
        "body": "Open the page where you want the assistant to appear. At the spot where you want it, select **Add Section**."
      },
      {
        "title": "Choose the HTML section",
        "body": "In the section list, search for **HTML** and select **Add**. This drops an empty custom-code block onto the page."
      },
      {
        "title": "Paste your embed code",
        "body": "Paste the snippet into the **Custom Code** field. Leave **Forced Height** blank for automatic sizing, or set a pixel height if you want a fixed chat area.",
        "placeSnippet": true
      },
      {
        "title": "Preview and publish",
        "body": "Use **Preview** to check the page, then select **Publish**. Custom code does not render inside the editor, so always confirm on the live published site. Repeat these steps on any other page where you want the assistant, since GoDaddy applies custom code per page."
      }
    ],
    "note": "GoDaddy wraps custom code in a sandboxed iframe and has no header or footer code area, so a site-wide floating widget cannot load. Use the inline embed and add it to each page where you want the assistant.",
    "docUrl": "https://www.godaddy.com/help/add-html-or-custom-code-to-my-site-27252"
  },
  {
    "key": "framer",
    "name": "Framer",
    "cardDesc": "Add the chat widget to your Framer site.",
    "category": "builder",
    "brand": "#0099FF",
    "logo": "/framer-logo.png",
    "embed": "widget",
    "intro": "Framer lets you drop a site-wide script into your project, so your Ultimo Bots AI agent appears as a floating chat bubble on every page. The whole setup takes a couple of minutes inside your project settings.",
    "planCaveat": "Custom code requires the Pro plan ($30/month) or higher. The Free and Basic plans do not include custom head or body code.",
    "steps": [
      {
        "title": "Open your project settings",
        "body": "In the Framer editor, open your site and go to **Project Settings**. In the left sidebar, select the **Custom Code** tab."
      },
      {
        "title": "Add a new script",
        "body": "Click **Add Script** (or the **+** icon if you already have snippets). Give it a clear name like `Ultimo Bots chat widget` so you can find it later."
      },
      {
        "title": "Set placement to End of body",
        "body": "For the location, choose the **End of <body>** slot. This loads the chat widget after your page content so your design stays fast while the assistant loads. Leave the execution set to **Once**."
      },
      {
        "title": "Paste your Ultimo Bots snippet",
        "body": "Paste the full code below into the snippet field. It includes both the container `<div>` and the `<script>` tag, which together render the floating chat bubble across your whole site.",
        "placeSnippet": true
      },
      {
        "title": "Save and publish",
        "body": "Close the settings panel, then click **Publish** in the top right to push the change live. Custom code only runs on the published site, so open your live URL to confirm the chat bubble appears."
      }
    ],
    "note": "Adding custom code needs a paid Framer Pro plan or higher. It is not available on the Free or Basic plans.",
    "docUrl": "https://www.framer.com/help/articles/how-to-add-custom-code/"
  },
  {
    "key": "weebly",
    "name": "Weebly",
    "cardDesc": "Add the chat widget to your Weebly site.",
    "category": "builder",
    "brand": "#0E76FD",
    "logo": "/weebly-logo.svg",
    "embed": "widget",
    "intro": "Weebly lets you drop a site-wide script into the Footer Code field, so your Ultimo Bots AI agent appears as a floating chat bubble on every page. Setup takes a couple of minutes.",
    "steps": [
      {
        "title": "Open your site editor",
        "body": "Sign in at **weebly.com**, then click **Edit Site** on the site you want the assistant on."
      },
      {
        "title": "Open the SEO settings",
        "body": "In the editor top menu, click **Settings**, then choose **SEO**. You will see two fields, **Header Code** and **Footer Code**. If your editor has no **Settings** tab, open **Website** then **General Settings** from your dashboard instead."
      },
      {
        "title": "Paste the snippet into Footer Code",
        "body": "Click inside the **Footer Code** field and paste the full snippet. This loads your AI agent on every page of the site.",
        "placeSnippet": true
      },
      {
        "title": "Save your settings",
        "body": "Click **Save** at the bottom of the SEO settings to store the code."
      },
      {
        "title": "Publish the site",
        "body": "Click **Publish** in the top right corner. Weebly does not show custom code in the editor preview, so publishing is required. Visit your live site and look for the chat bubble in the corner."
      }
    ],
    "note": "Weebly does not preview custom code in the editor. Publish the site to see the chat widget live.",
    "docUrl": "https://www.weebly.com/app/help/us/en"
  },
  {
    "key": "jimdo",
    "name": "Jimdo",
    "cardDesc": "Add the chat widget to your Jimdo site.",
    "category": "builder",
    "brand": "#00828C",
    "logo": "/jimdo-logo.svg",
    "embed": "widget",
    "intro": "Add the Ultimo Bots chat widget to your Jimdo Creator site with a small HTML snippet. Paste it into the site head so the AI agent floats on every page.",
    "planCaveat": "Requires a Jimdo Creator site. Custom HTML is not available on the no-code Jimdo Website Builder.",
    "steps": [
      {
        "title": "Open your Creator site in edit mode",
        "body": "Log in to Jimdo, select your **Creator** website, and click **Edit** to enter the editor. Custom code is a Creator feature, so the no-code Website Builder will not show these options."
      },
      {
        "title": "Open Edit Head",
        "body": "In the editor, open the **Settings** menu, then choose **Edit Head**. Code placed here loads on every page, so the chat widget appears site-wide instead of on a single page."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full code into the **Edit Head** field exactly as shown, with no edits, then click **Save**.",
        "placeSnippet": true
      },
      {
        "title": "Save and preview",
        "body": "Save your changes, then reload your live site. The floating chat bubble appears in the corner on every page."
      },
      {
        "title": "Single-page alternative",
        "body": "To place the assistant on one page only, hover over the content area and click **Add Element**, then **More Elements and Add-ons**, then **Widget/HTML**, and paste the same code into that element."
      }
    ],
    "note": "Custom code only works in Jimdo Creator. The newer no-code Jimdo Website Builder and Jimdo Dolphin sites do not allow pasted HTML or scripts.",
    "docUrl": "https://help.jimdo.com/hc/en-us/articles/115005505763-How-do-I-insert-my-own-code"
  },
  {
    "key": "duda",
    "name": "Duda",
    "cardDesc": "Add the chat widget to your Duda site.",
    "category": "builder",
    "brand": "#1f1f1f",
    "logo": "/duda-logo.png",
    "embed": "widget",
    "intro": "Duda lets you drop a site-wide script just before the closing body tag, so your Ultimo Bots assistant floats on every page. It takes about two minutes from the editor.",
    "steps": [
      {
        "title": "Open your site in the Duda editor",
        "body": "Log in to Duda and open the site you want to edit so you land on the visual editor with the left **side panel** visible."
      },
      {
        "title": "Open site Settings",
        "body": "In the left side panel click **More**, then click **Settings**. This opens the settings that apply to every page of your site. Note that **SEO/AEO** is now a separate item in the side panel, so use **Settings**, not SEO/AEO."
      },
      {
        "title": "Go to Body End HTML",
        "body": "Select **Body End HTML**. This field injects code right before the closing `</body>` tag on every page, which is exactly where a floating chat widget belongs. Do not use **Head HTML** for this script."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full snippet (the `<div>` and the `<script>`) into the **Body End HTML** box.",
        "placeSnippet": true
      },
      {
        "title": "Save and publish",
        "body": "Click **Save**, then click **Publish** to push the change live. Open your published site and the chat widget will appear in the corner on every page."
      }
    ],
    "docUrl": "https://support.duda.co/hc/en-us/articles/26519250445079-Custom-Code"
  },
  {
    "key": "carrd",
    "name": "Carrd",
    "cardDesc": "Add the chat widget to your Carrd site.",
    "category": "builder",
    "brand": "#000000",
    "logo": "/carrd-logo.svg",
    "embed": "widget",
    "intro": "Carrd lets you drop a custom code Embed element into your site so the Ultimo Bots chat widget loads as a floating bubble on every page. You set the Embed to load site-wide, just before the closing body tag.",
    "planCaveat": "Requires a Pro Standard plan or higher. The Embed element is not available on the free or Pro Lite plans.",
    "steps": [
      {
        "title": "Open your site in the editor",
        "body": "Sign in at Carrd and open the site you want the assistant on. Confirm you are on a **Pro Standard** plan or higher, since the **Embed** element is only available on those paid plans."
      },
      {
        "title": "Add an Embed element",
        "body": "Click the **+** button in the top-left of the editor to open the element picker, then choose **Embed**. It does not matter which section you drop it into, because you will set it to load site-wide in the next step."
      },
      {
        "title": "Set Type, Style, and placement",
        "body": "In the Embed element's settings, set **Type** to **Code**. Set **Style** to **Hidden**, then choose **Body End** for the placement. This loads the widget just before the closing `</body>` tag on every page, so the chat bubble appears site-wide."
      },
      {
        "title": "Paste your widget snippet into the Code field",
        "body": "Paste the full Ultimo Bots snippet into the **Code** box. Keep both lines together, the `<div>` and the `<script>`.",
        "placeSnippet": true
      },
      {
        "title": "Save and publish",
        "body": "Close the settings to apply the element, then click **Publish** in the top-right to push the change live. The widget does not render in the editor, so open your published URL to confirm the floating chat bubble appears."
      }
    ],
    "note": "The widget will not appear inside the editor. Publish your site to see the floating chat bubble live.",
    "docUrl": "https://carrd.co/docs/building/embedding-custom-code"
  },
  {
    "key": "strikingly",
    "name": "Strikingly",
    "cardDesc": "Add the chat widget to your Strikingly site.",
    "category": "builder",
    "brand": "#3F2A95",
    "logo": "/strikingly-logo.png",
    "embed": "widget",
    "intro": "Strikingly lets Pro and VIP sites add custom code, so you can drop in your Ultimo Bots AI agent as a floating chat bubble across your whole site. The Footer code area runs your snippet on every page.",
    "planCaveat": "Pro or VIP plan",
    "steps": [
      {
        "title": "Open your site editor",
        "body": "Log in at **strikingly.com**, then on your dashboard click **Edit Site** on the site you want the assistant on. This opens the editor where you manage pages and settings."
      },
      {
        "title": "Open Settings then Advanced",
        "body": "In the editor, open **Settings**, then choose the **Advanced** tab. This is where Strikingly keeps the **Custom Code** tools for Pro and VIP plans."
      },
      {
        "title": "Find the Custom Code Footer box",
        "body": "Under **Custom Code**, locate the **Footer** field. The **Footer** box is built for JavaScript and chat widgets, since it loads just before the closing `</body>` tag on every page. Do not use the **Header** box for this."
      },
      {
        "title": "Paste your snippet",
        "body": "Paste the full Ultimo Bots code into the **Footer** box exactly as given, with both the container and the script together. It loads site-wide as a floating chat bubble.",
        "placeSnippet": true
      },
      {
        "title": "Save and publish",
        "body": "Click **Save**, then click **Publish** in the top right to push the change live. Open your published site and look for the chat bubble in the lower corner."
      }
    ],
    "note": "Custom code is a paid feature. You need a Strikingly Pro or VIP plan to access the Footer code area.",
    "docUrl": "https://support.strikingly.com/hc/en-us/articles/215046517-Add-Custom-Code-HTML-CSS-and-JavaScript"
  },
  {
    "key": "tilda",
    "name": "Tilda",
    "cardDesc": "Add the chat widget to your Tilda site.",
    "category": "builder",
    "brand": "#1f1f1f",
    "logo": "/tilda-logo.png",
    "embed": "widget",
    "intro": "Tilda lets you drop a sitewide code snippet into your project settings, so your Ultimo Bots AI agent can greet visitors as a floating chat bubble on every page. Setup takes about two minutes.",
    "planCaveat": "Custom HTML and JavaScript requires a paid Tilda plan (Personal or Business). The free plan does not allow custom code.",
    "steps": [
      {
        "title": "Open your project settings",
        "body": "From your Tilda dashboard, open the site you want the assistant on, then click **Site Settings** at the top of the project page."
      },
      {
        "title": "Open the code injection area",
        "body": "In Site Settings, open the **More** tab, find **HTML code for the HEAD section**, and click **Edit Code**. Code added here loads on every page of your site."
      },
      {
        "title": "Paste your Ultimo Bots snippet",
        "body": "Paste the full snippet into the **Edit code inside HEAD tag** box, then click **Save** and **Close**. The `defer` script waits for the page to load, so the chat bubble mounts correctly on every page.",
        "placeSnippet": true
      },
      {
        "title": "Publish your site",
        "body": "Custom code only runs on the live site. Open **Edit Site**, then click **Publish All Pages** so the change goes live."
      },
      {
        "title": "Confirm it is live",
        "body": "Open your published Tilda site in a new browser tab. The Ultimo Bots chat widget should appear as a floating bubble in the corner. If you do not see it, confirm you are on a paid plan and that you republished all pages."
      }
    ],
    "note": "Custom code only runs on the live site, so republish all pages after saving. The code shows as text in the editor and preview until you publish.",
    "docUrl": "https://tilda.cc/en/answers/a/html-in-head-en/"
  },
  {
    "key": "webnode",
    "name": "Webnode",
    "cardDesc": "Add the chat widget to your Webnode site.",
    "category": "builder",
    "brand": "#0096B8",
    "logo": "/webnode-logo.svg",
    "embed": "widget",
    "intro": "Add your Ultimo Bots AI agent to Webnode by pasting one snippet into the site-wide HTML header/footer field. It loads a floating chat widget on every page in a few minutes.",
    "planCaveat": "Requires a paid plan (Standard, Profi, or Business). On the Standard or Profi plans a single paste in the HTML header/footer field applies to every page automatically.",
    "steps": [
      {
        "title": "Open your Website Settings",
        "body": "In the Webnode editor, click **Settings** in the top toolbar, then open **Website Settings**."
      },
      {
        "title": "Find the HTML header/footer field",
        "body": "On the right side of **Website Settings**, locate the **HTML header/footer** section. On the **Standard**, **Profi**, or **Business** plans this field applies to all pages at once."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full code into the **HTML header/footer** field. It adds a floating chat widget across your whole site.",
        "placeSnippet": true
      },
      {
        "title": "Save your changes",
        "body": "Click **Save** to store the code in your site settings."
      },
      {
        "title": "Publish and test",
        "body": "Click **Publish** to push the change live. Open your published site (the widget does not show in the editor preview) and look for the chat bubble in the lower corner."
      }
    ],
    "note": "The Free, Limited, and Mini plans cannot add site-wide HTML in one place, so the floating widget will not load on those plans without pasting the code into each page footer separately.",
    "docUrl": "https://www.webnode.com/support/add-html-into-header/"
  },
  {
    "key": "hostinger",
    "name": "Hostinger",
    "cardDesc": "Add the chat widget to your Hostinger site.",
    "category": "builder",
    "brand": "#673DE6",
    "logo": "/hostinger-logo.png",
    "embed": "widget",
    "intro": "Hostinger Website Builder lets you drop a site-wide snippet into the Custom code integration, so your Ultimo Bots assistant appears as a floating chat bubble on every page. The steps below take about two minutes.",
    "steps": [
      {
        "title": "Open the More menu",
        "body": "In the Hostinger Website Builder editor, click **More** (the **⋯** ellipsis button at the top) to open your website settings."
      },
      {
        "title": "Go to Integrations",
        "body": "Select **Integrations**, then choose **Custom code**. This is where Hostinger adds code that runs across your whole site, loaded into the page **<head>**."
      },
      {
        "title": "Paste the snippet into the Custom code field",
        "body": "Paste the snippet into the **Custom code** field. If you already have other snippets, add this one on its own line below them so it is not pasted inside another block.",
        "placeSnippet": true
      },
      {
        "title": "Save the code",
        "body": "Click **Save** to store the snippet in your Integrations settings."
      },
      {
        "title": "Publish your site",
        "body": "Click **Go live** in the top-right corner, or **Update website** if your site is already published. The chat widget does not render inside the editor, only on the published site and in preview, so open your live page to confirm the chat bubble appears."
      }
    ],
    "note": "Custom code runs only on your published site and in preview, not inside the editor. Open the live page to test.",
    "docUrl": "https://support.hostinger.com/en/articles/6445594-website-builder-how-to-integrate-custom-code"
  },
  {
    "key": "ionos",
    "name": "IONOS",
    "cardDesc": "Add the chat widget to your IONOS site.",
    "category": "builder",
    "brand": "#003D8F",
    "logo": "/ionos-logo.png",
    "embed": "widget",
    "intro": "IONOS Website Builder (MyWebsite Now) lets you add a custom code snippet that runs on every page from one place. That makes it simple to add your Ultimo Bots chat widget as a floating assistant across your whole site.",
    "planCaveat": "Custom HTML and JavaScript is not included in every MyWebsite Now package. If you do not see Custom Head Code, you may need to upgrade your plan to unlock it.",
    "steps": [
      {
        "title": "Open your website editor",
        "body": "Log in to IONOS, then in the title bar open **Menu > Websites & Stores** and click **Manage website** on your project. The Presence Suite opens. From its left menu select **Website > Website Editor** to launch the MyWebsite Now Editor."
      },
      {
        "title": "Go to sitewide code settings",
        "body": "In the editor's left-hand menu bar, select **Settings**, then choose **Custom Head Code**. Code added here runs on every page, which is exactly what a site-wide chat widget needs."
      },
      {
        "title": "Paste your snippet",
        "body": "Paste your Ultimo Bots code into the **Custom Head Code** text area. Placing it in this head field is fine. The script loads with `defer` and the widget renders across your whole site.",
        "placeSnippet": true
      },
      {
        "title": "Set the privacy consent option",
        "body": "Below the code field, choose **No consent required** so the assistant loads right away, or pick **Consent required** if your cookie policy needs it. Then click **Save**."
      },
      {
        "title": "Publish and check",
        "body": "Click **Publish** to push the change live, then open your published site. You should see the floating chat bubble in the corner on every page."
      }
    ],
    "note": "The HTML feature is not available on all MyWebsite Now plans. If Custom Head Code is missing from your editor Settings, your package may need an upgrade.",
    "docUrl": "https://www.ionos.com/help/websites-stores/editing-mywebsite-now-content/using-html-and-javascript/"
  },
  {
    "key": "site123",
    "name": "SITE123",
    "cardDesc": "Add the chat widget to your SITE123 site.",
    "category": "builder",
    "brand": "#0583F2",
    "logo": "/site123-logo.svg",
    "embed": "widget",
    "intro": "SITE123 lets you drop in custom code through its Plugins area, so your Ultimo Bots assistant can float on every page of your site. The steps below add the chat widget site-wide in a few minutes.",
    "planCaveat": "Custom code requires a paid SITE123 Premium plan. It is not available on the free plan.",
    "steps": [
      {
        "title": "Open your site settings",
        "body": "Log in to SITE123 and open your website in the editor. Click the **Settings** gear icon in the top toolbar."
      },
      {
        "title": "Go to Plugins",
        "body": "In the settings menu, select **Plugins**. This is where SITE123 manages add-ons and custom code snippets."
      },
      {
        "title": "Add a new custom code block",
        "body": "Click **Add New**, then choose **Add New Custom Code**. Enter a **Name** like `Ultimo Bots chat widget` so you can find it later, and set **Enable** to **Yes**."
      },
      {
        "title": "Choose where the code loads",
        "body": "Under **Set the code place**, select **Before closing the body tag**. This loads the chat widget on every page of your site."
      },
      {
        "title": "Paste the widget code",
        "body": "Paste the Ultimo Bots snippet into the code editor exactly as provided, keeping the `<div>` and `<script>` tags intact. Do not change or remove any part of it.",
        "placeSnippet": true
      },
      {
        "title": "Save and check your live site",
        "body": "Click **Save** to apply the change. Open your published site in a new tab and look for the floating chat bubble in the corner. If it does not appear right away, refresh the page or clear your browser cache."
      }
    ],
    "note": "Custom code is a Premium feature, so you need a paid SITE123 plan to add the widget.",
    "docUrl": "https://www.site123.com/support/7168957-add-plugins-or-custom-code"
  },
  {
    "key": "googlesites",
    "name": "Google Sites",
    "cardDesc": "Add the chat widget to your Google Sites site.",
    "category": "builder",
    "brand": "#4285F4",
    "logo": "/googlesites-logo.svg",
    "embed": "iframe",
    "intro": "Google Sites lets you drop your Ultimo Bots assistant onto a page with its built-in Embed tool. It runs the embed inside a sandboxed frame, so you place the chat inline on a page rather than as a site-wide bubble.",
    "steps": [
      {
        "title": "Open your page in the editor",
        "body": "Go to [Google Sites](https://sites.google.com), open the site you want to edit, and select the page where the assistant should appear. The chat shows inline on this page only."
      },
      {
        "title": "Open the Embed tool",
        "body": "In the **Insert** panel on the right, click **Embed**. In the dialog, select the **Embed code** tab (not **By URL**)."
      },
      {
        "title": "Paste your embed code",
        "body": "Paste the snippet into the **Embed code** box, click **Next** to preview, then click **Insert** to drop it onto the page.",
        "placeSnippet": true
      },
      {
        "title": "Size and position the assistant",
        "body": "Drag the inserted block to where you want it and resize it so the chat has room to display. A taller box gives visitors a better view of the conversation."
      },
      {
        "title": "Publish your site",
        "body": "At the top right, click **Publish** to push the change live. Open your published page to confirm the assistant loads and responds."
      }
    ],
    "note": "Google Sites runs embedded code in a sandboxed iframe, so a site-wide floating bubble cannot load. Use the inline embed on each page where you want the assistant to appear.",
    "docUrl": "https://support.google.com/sites/answer/90569?hl=en"
  },
  {
    "key": "shopify",
    "name": "Shopify",
    "cardDesc": "Add the chat widget to your Shopify store.",
    "category": "ecommerce",
    "brand": "#95BF47",
    "logo": "/shopify-logo.svg",
    "embed": "widget",
    "intro": "Shopify lets you edit your theme code directly, so you can add the Ultimo Bots chat widget once and have the AI agent appear on every page of your store. The floating chat bubble loads site-wide from a single snippet.",
    "steps": [
      {
        "title": "Open your theme list",
        "body": "From your Shopify admin, go to **Online Store** > **Themes**. Your live theme appears at the top of the page."
      },
      {
        "title": "Open the code editor",
        "body": "On your live theme, click the **...** (three dots) menu button, then choose **Edit code**. This opens the theme code editor with your theme files in the left sidebar."
      },
      {
        "title": "Open theme.liquid",
        "body": "In the left sidebar, open the **Layout** folder and click **theme.liquid**. This file wraps every page of your store."
      },
      {
        "title": "Paste the snippet before the closing body tag",
        "body": "Scroll to the bottom of the file and place your cursor on the line just before the closing `</body>` tag. Paste the Ultimo Bots snippet there so the chat widget loads on every page.",
        "placeSnippet": true
      },
      {
        "title": "Save and check your store",
        "body": "Click **Save** in the top right. Open your storefront in a new tab and confirm the chat bubble appears in the corner on any page."
      }
    ],
    "note": "Tip: before editing, back up your theme. On the **Themes** page, click the **...** menu on your live theme and choose **Duplicate**.",
    "docUrl": "https://help.shopify.com/en/manual/online-store/themes/customizing-themes/edit-code/edit-theme-code"
  },
  {
    "key": "woocommerce",
    "name": "WooCommerce",
    "cardDesc": "Add the chat widget to your WooCommerce store.",
    "category": "ecommerce",
    "brand": "#7F54B3",
    "logo": "/woocommerce-logo.png",
    "embed": "widget",
    "intro": "WooCommerce runs on WordPress, so you can add the Ultimo Bots chat widget across your whole store with a small snippet in your site-wide footer code. The free WPCode plugin gives you a safe place to paste it without editing theme files, and the floating assistant then appears on every page.",
    "steps": [
      {
        "title": "Install a footer code plugin",
        "body": "In your WordPress dashboard, go to **Plugins > Add New Plugin**, search for **WPCode**, then click **Install Now** and **Activate**. This free plugin lets you add site-wide code without editing theme files."
      },
      {
        "title": "Open the Header & Footer settings",
        "body": "In the left sidebar, go to **Code Snippets > Header & Footer**. You will see three boxes labeled **Header**, **Body**, and **Footer**."
      },
      {
        "title": "Paste your snippet into the Footer box",
        "body": "Paste the Ultimo Bots code into the **Footer** box. This loads the chat widget just before the closing body tag on every page of your store.",
        "placeSnippet": true
      },
      {
        "title": "Save your changes",
        "body": "Click **Save Changes** in the top right to publish the code across your site."
      },
      {
        "title": "Check your live store",
        "body": "Open your store in a new tab and look for the chat bubble in the bottom corner. If you use a caching plugin, clear the cache first so the widget appears."
      }
    ],
    "note": "Some themes include a built-in footer or custom code area. If yours does, you can paste there instead of installing a plugin.",
    "docUrl": "https://wpcode.com/docs/using-the-global-header-footer-settings/"
  },
  {
    "key": "squareonline",
    "name": "Square Online",
    "cardDesc": "Add the chat widget to your Square Online store.",
    "category": "ecommerce",
    "brand": "#006AFF",
    "logo": "/squareonline-logo.svg",
    "embed": "widget",
    "intro": "Square Online lets you add a site-wide code snippet to your store, so your Ultimo Bots AI agent appears as a floating chat bubble on every page. You add it once from your Square Dashboard, then publish.",
    "steps": [
      {
        "title": "Open your site settings",
        "body": "Sign in to your **Square Dashboard** and go to **Websites**. Select the site you want, then open **Settings**."
      },
      {
        "title": "Go to Tracking Tools",
        "body": "Under **Settings**, select **Tracking Tools**. This is where Square stores site-wide custom code that runs on every page."
      },
      {
        "title": "Add a new code block",
        "body": "Select **Add new code**. In the pop-up, enter a name like `Ultimo Bots chat widget` so you can find it later."
      },
      {
        "title": "Paste the snippet at the end of the body",
        "body": "Paste your Ultimo Bots snippet into the code field, then set the placement to **End of body** so the chat widget loads on every page. Select **Save**.",
        "placeSnippet": true
      },
      {
        "title": "Publish your site",
        "body": "Open the site editor and select **Publish** to push the change live. Visit your store and the chat bubble should appear in the corner of every page."
      }
    ],
    "docUrl": "https://squareup.com/help/us/en/article/6957-add-custom-tracking-code-to-your-website"
  },
  {
    "key": "bigcommerce",
    "name": "BigCommerce",
    "cardDesc": "Add the chat widget to your BigCommerce store.",
    "category": "ecommerce",
    "brand": "#121118",
    "logo": "/bigcommerce-logo.svg",
    "embed": "widget",
    "intro": "BigCommerce has a built-in Script Manager, so you can add the Ultimo Bots chat widget across your whole store without touching theme files. Paste one snippet and your AI agent goes live on every page.",
    "steps": [
      {
        "title": "Open Script Manager",
        "body": "In your BigCommerce control panel, click **Storefront** in the left sidebar, then click **Script Manager**."
      },
      {
        "title": "Create a new script",
        "body": "Click **Create a Script** in the top right. In **Name of script**, enter something clear like `Ultimo Bots chat widget` so you can find it later."
      },
      {
        "title": "Set placement and pages",
        "body": "Set **Location on page** to **Footer** and **Select pages where to add script** to **All pages** so the floating chat bubble shows site-wide. Set **Script category** to **Functional** and **Script type** to **Script**."
      },
      {
        "title": "Paste the snippet",
        "body": "In the **Script contents** box, paste the full Ultimo Bots code (the `<div>` plus the `<script>` tag).",
        "placeSnippet": true
      },
      {
        "title": "Save and check your store",
        "body": "Click **Save**, then open your live storefront in a new tab. The chat bubble should appear in the corner. If you do not see it, hard-refresh the page or clear your browser cache."
      }
    ],
    "docUrl": "https://support.bigcommerce.com/s/article/Using-Script-Manager"
  },
  {
    "key": "magento",
    "name": "Magento",
    "cardDesc": "Add the chat widget to your Magento store.",
    "category": "ecommerce",
    "brand": "#EB5202",
    "logo": "/magento-logo.svg",
    "embed": "widget",
    "intro": "Adobe Commerce and Magento Open Source let you add a site-wide script right before the closing body tag, so your Ultimo Bots AI agent appears as a floating chat bubble on every storefront page. You add it once in the admin Design Configuration, with no theme files to touch.",
    "steps": [
      {
        "title": "Open Design Configuration",
        "body": "In the Admin sidebar, go to **Content** > **Design** > **Configuration**. This controls the storefront layout for your whole site."
      },
      {
        "title": "Edit your store view",
        "body": "Find the store view you want the chat widget on (usually the **Default Store View** or your main website row) and click **Edit** in the **Action** column."
      },
      {
        "title": "Open the Footer section",
        "body": "Scroll to **Other Settings** and expand the **Footer** section. The field you want is **Miscellaneous HTML**, which loads just before the closing `</body>` tag on every page."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full snippet into the **Miscellaneous HTML** box. Both the `<div>` and the `<script>` lines belong here together.",
        "placeSnippet": true
      },
      {
        "title": "Save the configuration",
        "body": "Click **Save Configuration** at the top right. If your store uses Content Security Policy, also allow the script domain so it is not blocked on **Checkout** pages."
      },
      {
        "title": "Check your storefront",
        "body": "Open your live store in a new tab and refresh. If the bubble does not appear, clear the cache under **System** > **Cache Management** and reload."
      }
    ],
    "note": "JavaScript added here must be allowed in your Content Security Policy (CSP), or it will be blocked on Checkout and payment pages. CSP runs in restrict mode by default on Adobe Commerce 2.4.7 and later.",
    "docUrl": "https://experienceleague.adobe.com/en/docs/commerce-admin/content-design/design/page-setup"
  },
  {
    "key": "ecwid",
    "name": "Ecwid",
    "cardDesc": "Add the chat widget to your Ecwid store.",
    "category": "ecommerce",
    "brand": "#0096C7",
    "logo": "/ecwid-logo.png",
    "embed": "widget",
    "intro": "Ecwid by Lightspeed lets you add custom JavaScript to your Instant Site, so your Ultimo Bots AI agent appears as a floating chat bubble on every page. The whole setup takes a couple of minutes inside your store admin.",
    "planCaveat": "Custom code requires a paid plan (Venture, Business, or Unlimited). It is not available on the free plan.",
    "steps": [
      {
        "title": "Open your Instant Site settings",
        "body": "Log in to your Ecwid admin and click **Website** in the left menu. If you do not see it, go to **Overview** and click **Manage Instant Site**."
      },
      {
        "title": "Find the Advanced website settings",
        "body": "Scroll to the bottom of the **Website** page to the **Advanced website settings** section. This is where Ecwid lets you inject site-wide code."
      },
      {
        "title": "Open the Custom JavaScript editor",
        "body": "Under **Custom JavaScript code**, click **Add Code**. Code added to the body here runs on every page of your site, which is exactly where the floating chat widget belongs."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full Ultimo Bots code into the **Custom JavaScript code** field, then click **Save**.",
        "placeSnippet": true
      },
      {
        "title": "Check your live site",
        "body": "Open your live Instant Site and refresh the page. The chat widget bubble should appear in the corner on every page."
      }
    ],
    "note": "Custom JavaScript is capped at 4000 characters, which the Ultimo Bots snippet fits within easily.",
    "docUrl": "https://support.ecwid.com/hc/en-us/articles/8359349900828-Adding-custom-code-to-Instant-Site"
  },
  {
    "key": "prestashop",
    "name": "PrestaShop",
    "cardDesc": "Add the chat widget to your PrestaShop store.",
    "category": "ecommerce",
    "brand": "#DF0067",
    "logo": "/prestashop-logo.svg",
    "embed": "widget",
    "intro": "PrestaShop core has no built-in footer code field, so the simplest path is a free HTML block module that drops your snippet onto every page. Once it is in place, your Ultimo Bots assistant floats on every page of your store.",
    "steps": [
      {
        "title": "Install a free HTML block module",
        "body": "In your PrestaShop admin, open **Modules > Module Manager**. Use the **Marketplace** tab to search for a free custom HTML block module such as **HTML Box**, then install it. This gives you a place to add code without editing theme files."
      },
      {
        "title": "Open the module and add a new block",
        "body": "Click **Configure** on the module, then add a new HTML block. When asked for the editor type, pick the **source code** (HTML) option so your snippet is saved exactly as pasted and not altered by the rich text editor."
      },
      {
        "title": "Set the location to every page",
        "body": "For the block's hook or **Position**, choose **displayBeforeBodyClosingTag**. This loads the block on every page right before the closing `</body>` tag, which is exactly where the chat widget belongs. If that hook is not listed by your theme, choose **displayFooter** instead."
      },
      {
        "title": "Paste your Ultimo Bots snippet",
        "body": "In the source code field, paste the snippet below, then **Save** the block.",
        "placeSnippet": true
      },
      {
        "title": "Clear the cache and check your store",
        "body": "Go to **Advanced Parameters > Performance** and click **Clear cache**, then open your live storefront. The chat bubble should appear in the lower corner on every page."
      }
    ],
    "note": "PrestaShop core has no native site-wide code box, so you install a free HTML block module to host the snippet. The displayBeforeBodyClosingTag hook is theme based, so on some custom themes only displayFooter may be available.",
    "docUrl": "https://devdocs.prestashop-project.org/8/modules/concepts/hooks/list-of-hooks/displaybeforebodyclosingtag/"
  },
  {
    "key": "opencart",
    "name": "OpenCart",
    "cardDesc": "Add the chat widget to your OpenCart store.",
    "category": "ecommerce",
    "brand": "#0E92C7",
    "logo": "/opencart-logo.svg",
    "embed": "widget",
    "intro": "OpenCart lets you drop a site-wide script through an Analytics extension, so your Ultimo Bots AI agent appears as a floating chat bubble on every storefront page. No theme files or FTP needed.",
    "steps": [
      {
        "title": "Log in to your OpenCart admin",
        "body": "Open your store's admin dashboard, usually at `yourdomain.com/admin`, and sign in with your administrator account."
      },
      {
        "title": "Open the Analytics extensions list",
        "body": "In the left sidebar go to **Extensions** then **Extensions**. At the top, open the **Choose the extension type** dropdown and select **Analytics**."
      },
      {
        "title": "Install an Analytics extension",
        "body": "If an Analytics extension is listed (OpenCart 3 includes **Google Analytics**), click the green **Install** (plus) button next to it. If the list is empty (OpenCart 4), first install the free **Third Party JS** extension by uploading its `.ocmod.zip` under **Extensions** then **Installer**, then return here."
      },
      {
        "title": "Open the code field",
        "body": "Click the blue **Edit** (pencil) button next to the installed Analytics extension. This opens a form with a large code field, often labeled **Code** or **Google Analytics Code**.",
        "placeSnippet": true
      },
      {
        "title": "Paste the snippet and enable it",
        "body": "Paste the full Ultimo Bots snippet into the code field. Set **Status** to **Enabled**, then click the blue **Save** button in the top right."
      },
      {
        "title": "Check your storefront",
        "body": "Open your live store in a new tab and look for the chat bubble in the bottom corner. Refresh once if it does not appear. If your store uses caching, clear it from the admin and reload."
      }
    ],
    "note": "An Analytics-type extension injects the snippet on all storefront pages. OpenCart 4 ships with no Analytics extension by default, so install the free \"Third Party JS\" extension from the OpenCart marketplace first. OpenCart 3 already includes a \"Google Analytics\" Analytics extension you can use.",
    "docUrl": "https://webocreation.com/how-to-add-an-analytics-extension-in-opencart-4-third-party-js-free-opencart-extension"
  },
  {
    "key": "bigcartel",
    "name": "Big Cartel",
    "cardDesc": "Add the chat widget to your Big Cartel store.",
    "category": "ecommerce",
    "brand": "#10100B",
    "logo": "/bigcartel-logo.svg",
    "embed": "widget",
    "intro": "Big Cartel lets you drop a site-wide script into its Integration Code area, so your Ultimo Bots AI agent shows up as a floating chat bubble on every page of your shop. The code lives outside your theme, so it stays put even if you switch designs.",
    "planCaveat": "A paid plan (Platinum or Diamond). The free Gold plan only includes CSS editing, not Integration Code.",
    "steps": [
      {
        "title": "Open your shop's code settings",
        "body": "Log in at **app.bigcartel.com**, go to **Shop settings**, then click **Shop designers**. Open the **Code** tab."
      },
      {
        "title": "Find the Integration Code area",
        "body": "Scroll to the **Integration Code** section. This is the right place for third-party scripts, and your code here survives theme switches and updates."
      },
      {
        "title": "Choose the Body placement",
        "body": "Under **Integration Code**, click **Body** so your code is inserted just before the closing `</body>` tag. This loads the chat widget on every page of your shop.",
        "placeSnippet": true
      },
      {
        "title": "Paste and save",
        "body": "Paste the snippet into the **Body** field, then click **Done** to save your code."
      },
      {
        "title": "Check your shop",
        "body": "Open your live storefront in a new tab and refresh. The chat bubble should appear in the corner. If you don't see it, confirm you are on a paid plan, since the free **Gold** plan does not include Integration Code."
      }
    ],
    "note": "Use Integration Code, not the theme code editor. Integration Code stays in place when you switch or update your theme.",
    "docUrl": "https://www.bigcartel.com/resources/help/article/integration-code"
  },
  {
    "key": "joomla",
    "name": "Joomla",
    "cardDesc": "Add the chat widget to your Joomla site.",
    "category": "cms",
    "brand": "#5091CD",
    "logo": "/joomla-logo.svg",
    "embed": "widget",
    "intro": "Joomla lets you drop the Ultimo Bots snippet into a Custom HTML module that shows on every page, so your AI agent floats across the whole site. The one thing to watch for is Joomla's text filter, which strips scripts unless you turn it off for your user group.",
    "steps": [
      {
        "title": "Allow scripts in your content",
        "body": "In the admin panel open **System**, then under **Global Configuration** open the **Text Filters** tab. Set your group (usually **Super Users**) to **No Filtering** and click **Save**. This stops Joomla from removing the widget script."
      },
      {
        "title": "Create a new Custom HTML module",
        "body": "Open **System**, then under **Manage** click **Site Modules**. Click **New** and choose **Custom HTML** from the module type list."
      },
      {
        "title": "Paste the snippet into the editor",
        "body": "In the module editor, click the **Toggle Editor** button below the content box to switch to code view, then paste the Ultimo Bots snippet. Give the module a title like `Ultimo Bots` and set **Show Title** to **Hide**.",
        "placeSnippet": true
      },
      {
        "title": "Show it on every page",
        "body": "Set a **Position** that your template renders site-wide (for example **footer** in the default Cassiopeia template), set **Status** to **Published**, then open the **Menu Assignment** tab and choose **On all pages**."
      },
      {
        "title": "Save and check your site",
        "body": "Click **Save & Close**, then open **System** and run **Clear Cache**. Open your live site in a private window and the chat bubble should appear in the corner."
      }
    ],
    "note": "Joomla strips scripts from module content unless text filtering is set to No Filtering for your user group, so do not skip the text filter step.",
    "docUrl": "https://docs.joomla.org/Help5.x:Site_Modules:_Custom"
  },
  {
    "key": "drupal",
    "name": "Drupal",
    "cardDesc": "Add the chat widget to your Drupal site.",
    "category": "cms",
    "brand": "#0678BE",
    "logo": "/drupal-logo.svg",
    "embed": "widget",
    "intro": "Drupal lets you add a site-wide block with custom HTML so your Ultimo Bots assistant appears as a floating chat bubble on every page. A content block placed in a footer region is the simplest path for a site administrator and works on every modern Drupal version.",
    "steps": [
      {
        "title": "Open the content blocks list",
        "body": "Sign in as an administrator. In the **Manage** admin menu go to **Content**, open the **Blocks** tab, then click **Add content block**."
      },
      {
        "title": "Create the block with a script-allowing format",
        "body": "In **Block description** enter a name like `Ultimo Bots widget`. Below the **Body** field, set **Text format** to one that permits script tags, such as a plain format with no editor or a **Full HTML** format whose \"Limit allowed HTML tags\" filter has been removed. If a rich-text editor is shown, switch it to **Source** view first so it does not rewrite your code."
      },
      {
        "title": "Paste the Ultimo Bots snippet",
        "body": "Paste the full snippet below into the **Body** field exactly as given, keeping the `<div>` and the `<script>` tag together. Then click **Save**.",
        "placeSnippet": true
      },
      {
        "title": "Place the block site-wide",
        "body": "Go to **Structure** then **Block Layout**. Find a region that renders on every page, such as **Footer**, and click **Place block** next to it. Search for your block name, click **Place block**, leave the visibility conditions empty so it loads everywhere, and save."
      },
      {
        "title": "Clear caches and check your site",
        "body": "Go to **Configuration** then under **Development** open **Performance** and click **Clear all caches**. Open your site in a new tab and look for the chat bubble in the lower corner, then click it to confirm your assistant opens and replies."
      }
    ],
    "note": "Drupal filters out script tags by default. Use a text format that allows scripts (a format with no rich-text editor, or one where the \"Limit allowed HTML tags\" filter is removed), otherwise the snippet is stripped and the widget will not load. Restrict any script-allowing format to administrators. Developers can instead paste the snippet just before the closing body tag in their theme's html.html.twig.",
    "docUrl": "https://www.drupal.org/docs/user_guide/en/block-create-custom.html"
  },
  {
    "key": "ghost",
    "name": "Ghost",
    "cardDesc": "Add the chat widget to your Ghost site.",
    "category": "cms",
    "brand": "#15171A",
    "logo": "/ghost-logo.png",
    "embed": "widget",
    "intro": "Ghost has a built-in Code Injection tool, so adding your Ultimo Bots chat widget takes about a minute. Paste the snippet once into the Site Footer and the assistant appears on every page of your publication.",
    "steps": [
      {
        "title": "Open your Ghost admin",
        "body": "Sign in to your site at **yoursite.com/ghost** to reach the Ghost admin dashboard."
      },
      {
        "title": "Go to Code injection",
        "body": "Click **Settings** (the gear icon) in the bottom of the left sidebar, scroll to the **Advanced** section, and open **Code injection**."
      },
      {
        "title": "Paste the snippet into Site Footer",
        "body": "Find the **Site Footer** box and paste the snippet there. Ghost outputs this code just before the closing `</body>` tag on every page, which is exactly where the chat widget belongs. Leave the **Site Header** box empty.",
        "placeSnippet": true
      },
      {
        "title": "Save your changes",
        "body": "Click **Save** in the top right corner of the Code injection screen."
      },
      {
        "title": "Check your live site",
        "body": "Open your published site in a new tab and refresh. The chat bubble appears in the bottom corner across your whole publication. If you do not see it, do a hard refresh to clear the cache."
      }
    ],
    "docUrl": "https://ghost.org/tutorials/use-code-injection-in-ghost/"
  },
  {
    "key": "hubspot",
    "name": "HubSpot CMS",
    "cardDesc": "Add the chat widget to your HubSpot CMS site.",
    "category": "cms",
    "brand": "#FF7A59",
    "logo": "/hubspot-logo.svg",
    "embed": "widget",
    "intro": "HubSpot CMS lets you add a site-wide script through its Site Footer HTML setting, so your Ultimo Bots AI agent loads as a floating chat widget on every page. Paste one snippet once and you are live across the whole site.",
    "steps": [
      {
        "title": "Open your settings",
        "body": "In your HubSpot account, click the **settings** icon (the cog) in the top navigation bar."
      },
      {
        "title": "Go to Content > Pages",
        "body": "In the left sidebar, navigate to **Content** > **Pages**. This is where HubSpot keeps the site-wide header and footer code."
      },
      {
        "title": "Choose the domain to apply it to",
        "body": "Open the **Choose a domain to edit its settings** dropdown. Select **Default settings for all domains** to load the widget everywhere, or pick a specific domain and click **Override default settings** so the code saves for it."
      },
      {
        "title": "Find the Site Footer HTML field",
        "body": "Scroll to the **Site Footer HTML** section. Code added here loads right before the closing `</body>` tag on every page, which is exactly where a floating chat widget belongs.",
        "placeSnippet": true
      },
      {
        "title": "Save and check your site",
        "body": "Click **Save**, then open your live site and refresh. The chat bubble should appear in the corner of every page. It can take a minute for changes to publish."
      }
    ],
    "docUrl": "https://knowledge.hubspot.com/website-and-landing-pages/use-code-snippets-with-hubspot-content"
  },
  {
    "key": "typo3",
    "name": "TYPO3",
    "cardDesc": "Add the chat widget to your TYPO3 site.",
    "category": "cms",
    "brand": "#FF8700",
    "logo": "/typo3-logo.svg",
    "embed": "widget",
    "intro": "TYPO3 lets you load a site-wide script through your TypoScript template, so your Ultimo Bots chat widget appears as a floating bubble on every page. You add one small snippet to the page footer output.",
    "steps": [
      {
        "title": "Open the TypoScript module",
        "body": "In the TYPO3 backend, go to **Site Management > TypoScript** and open **Edit TypoScript Record**. On older installs this lives under **Web > Template**. Select your root page (the one with the template or globe icon) in the page tree."
      },
      {
        "title": "Edit the template record",
        "body": "Click **Edit** (the pencil) to open the full TypoScript record, then scroll to the **Setup** field where your site-wide TypoScript lives."
      },
      {
        "title": "Add the widget to the footer output",
        "body": "In the **Setup** field, add a `page.footerData` block so the code renders just before the closing `</body>` tag on every page. Use the next free numeric index if entries already exist. Paste the snippet inside a `TEXT` object's `value`, for example `page.footerData.10 = TEXT` then `page.footerData.10.value (` your snippet `)`.",
        "placeSnippet": true
      },
      {
        "title": "Save the record",
        "body": "Click **Save** in the top toolbar. Then go to **Admin Tools > Maintenance** and click **Flush cache** so the new footer code goes live."
      },
      {
        "title": "Confirm the widget is live",
        "body": "Open your public website in a new tab. The chat bubble should appear in the lower corner on every page. If it does not show, clear your browser cache and reload."
      }
    ],
    "docUrl": "https://docs.typo3.org/m/typo3/reference-typoscript/main/en-us/Guide/Page/Index.html"
  }
];
